import { Injectable, Injector } from '@angular/core';
import { RouteReuseStrategy, ActivatedRouteSnapshot, DetachedRouteHandle } from '@angular/router';
import { UserService } from '../services/user-service.service';

@Injectable()
export class CustomRouteReuseStrategy implements RouteReuseStrategy {
  private storedRoutes = new Map<string, DetachedRouteHandle>();

  // Routes that should be cached - simplified to avoid recursion
  private cacheableRoutes = [
    'messages',
    'posts',
    'directory',
    'groups',
    'calendar',
    'profiles',
    'feed'
  ];

  // The active user's id, used to detect a user switch (impersonation start/stop).
  private currentUserId: number | null = null;

  constructor(injector: Injector) {
    Promise.resolve().then(() => {
      const userService = injector.get(UserService);
      userService.currentUserProfile$.subscribe(profile => {
        const newUserId = profile?.Id ?? null;
        if (newUserId === null) {
          return;
        }
        if (this.currentUserId !== null && newUserId !== this.currentUserId) {
          this.clearRouteCache();
        }
        this.currentUserId = newUserId;
      });
    });
  }

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    // Only cache leaf routes to avoid recursion
    if (!route.routeConfig || !route.routeConfig.path) {
      return false;
    }
    
    const path = route.routeConfig.path;
    return this.cacheableRoutes.includes(path) && this.isLeafRoute(route);
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
    if (handle && route.routeConfig && route.routeConfig.path) {
      const routeKey = this.getRouteKey(route);
      this.storedRoutes.set(routeKey, handle);
    }
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    if (!route.routeConfig || !route.routeConfig.path) {
      return false;
    }
    
    const routeKey = this.getRouteKey(route);
    return this.storedRoutes.has(routeKey);
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    if (!route.routeConfig || !route.routeConfig.path) {
      return null;
    }
    
    const routeKey = this.getRouteKey(route);
    return this.storedRoutes.get(routeKey) || null;
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    // When navigating away from the posts section, clear the cached feed so it
    // gets a fresh load next time the user enters posts.
    if (curr.routeConfig?.path === 'posts' && future.routeConfig?.path !== 'posts') {
      this.storedRoutes.delete('feed');
    }

    // If both routes use the same component, always reuse the instance.
    // This prevents destroy/recreate when navigating between sub-routes of the same
    // feature (e.g. /messages → /messages/:chatId), since those components manage
    // state reactively via paramMap and service observables.
    if (future.routeConfig?.component && curr.routeConfig?.component) {
      return future.routeConfig.component === curr.routeConfig.component;
    }
    return future.routeConfig === curr.routeConfig;
  }

  private getRouteKey(route: ActivatedRouteSnapshot): string {
    // Simple key generation to avoid recursion
    if (!route.routeConfig || !route.routeConfig.path) {
      return '';
    }
    
    // Include params to differentiate between routes with different parameters
    const params = Object.keys(route.params).length > 0 ? 
      '_' + Object.values(route.params).join('_') : '';
    
    return route.routeConfig.path + params;
  }

  private isLeafRoute(route: ActivatedRouteSnapshot): boolean {
    // Check if this is a leaf route (no children)
    return !route.children || route.children.length === 0;
  }

  // Method to clear cache for specific routes (can be called from services)
  clearRouteCache(routePath?: string): void {
    if (routePath) {
      const handle = this.storedRoutes.get(routePath);
      this.destroyHandle(handle);
      this.storedRoutes.delete(routePath);
    } else {
      // Clear all cached routes
      this.storedRoutes.forEach(handle => this.destroyHandle(handle));
      this.storedRoutes.clear();
    }
  }

  private destroyHandle(handle: DetachedRouteHandle | undefined): void {
    (handle as any)?.componentRef?.destroy();
  }
}